/**
 * 장애 감시.
 *
 * 서버가 1분마다 스스로 점검해 남긴 장애 기록을 본다. 같은 기록이 서버 로그에도 남는다
 * (`장애 발생`·`장애 해소`) — 이 화면이 열려 있지 않아도, 외부 감시 도구가 없어도 기록은 쌓인다.
 *
 * 알림 발송(메일·메신저·SMS)의 채널 설정은 여기 없다 — 비밀(메일 비밀번호·웹훅 주소·SMS 키)을 담는 설정이라
 * 개발자 전용 플러그인(`Plugins.tsx`)으로 옮겼다. 여기서는 발송 결과를 기록 옆에 보여 준다.
 *
 * **단계(베타·정식)를 여기 적지 않는다.** 그것은 이제 모듈 상태이고 개발자가 플러그인 화면에서 바꾼다 —
 * 화면마다 베껴 적으면 한쪽만 남아 거짓이 된다(`services/modules.ts`).
 */
import { useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AlertChannel, AlertCheckResult, AlertList, AlertRecord, SignageOneClient } from '../api/client.js';
import { Badge, ErrorNote, Loading, Panel, relativeTime } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { useAction, useAsync, usePolling } from '../hooks.js';
import { ColumnHead, FilterMenu, FilterNote, useTableSort } from '../components/TableHead.js';
import { applyFilters, filterOptions, fixedOrder, sortRows, type SortDir, type SortValue } from '../components/tableView.js';
import { useUrlQuery } from '../components/urlState.js';

const CHANNEL_LABEL: Readonly<Record<AlertChannel, string>> = { email: '메일', messenger: '메신저', sms: 'SMS' };

const TRANSITION_LABEL: Readonly<Record<AlertCheckResult['transition'], string>> = {
  fired: '신규 발생',
  resolved: '해소',
  ongoing: '지속',
  renotified: '추가 발생',
  pending: '관찰 중',
  none: '정상',
};

/**
 * 점검 문턱 한 줄 — 무엇을 언제 장애로 부르는가. 꺼 둔 규칙(0)은 꺼짐으로 적는다. 옛 서버는 뒤의 넷을 주지 않는다.
 */
export function thresholdsLine(monitor: AlertList['monitor']): string {
  const t = monitor.thresholds;
  const parts = [`점검 주기 ${monitor.interval_sec}초`, `오프라인 ${Math.round(t.offline_ratio * 100)}% 초과 ${t.offline_minutes}분`];
  if (t.display_offline_minutes !== undefined) {
    parts.push(t.display_offline_minutes > 0 ? `꺼진 화면 ${t.display_offline_minutes}분` : '꺼진 화면 경보 꺼짐');
  }
  parts.push(`증빙 끊김 ${t.proof_stall_minutes}분`, `API 오류 ${Math.round(t.api_error_rate * 1000) / 10}% 초과`);
  if (t.disk_free_percent !== undefined) parts.push(t.disk_free_percent > 0 ? `디스크 여유 ${t.disk_free_percent}% 미만` : '디스크 경보 꺼짐');
  if (t.backup_stale_hours !== undefined) parts.push(`백업 ${t.backup_stale_hours}시간째 성공 없음`);
  if (t.cert_days !== undefined) parts.push(t.cert_days > 0 ? `인증서 만료 ${t.cert_days}일 전` : '인증서 경보 꺼짐');
  return parts.join(' · ');
}

/** 꺼진 화면 장애에 실린 화면들(서버가 앞의 50대까지 싣는다). 다른 규칙이면 빈 목록. */
export function offlineDisplays(alert: Pick<AlertRecord, 'rule' | 'value'>): Array<{ id: string; name: string; location: string }> {
  if (alert.rule !== 'display_offline') return [];
  const rows = (alert.value as { displays?: unknown }).displays;
  if (!Array.isArray(rows)) return [];
  return rows.filter(
    (row): row is { id: string; name: string; location: string } =>
      Boolean(row) && typeof (row as { id?: unknown }).id === 'string' && typeof (row as { name?: unknown }).name === 'string',
  ).map((row) => ({ id: row.id, name: row.name, location: typeof row.location === 'string' ? row.location : '' }));
}

function when(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return `${date.toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })} (${relativeTime(iso)})`;
}

function durationMinutes(alert: AlertRecord, now: Date = new Date()): number {
  const end = alert.resolved_at ? new Date(alert.resolved_at) : now;
  return Math.max(0, Math.round((end.getTime() - new Date(alert.started_at).getTime()) / 60_000));
}

function duration(alert: AlertRecord): string {
  const minutes = durationMinutes(alert);
  return minutes < 60 ? `${minutes}분` : `${Math.floor(minutes / 60)}시간 ${minutes % 60}분`;
}

export const ALERT_SORT_KEYS = ['started', 'duration'] as const;
export type AlertSortKey = (typeof ALERT_SORT_KEYS)[number];

export function alertSortValue(alert: AlertRecord, key: AlertSortKey, now: Date = new Date()): SortValue {
  return key === 'started' ? Date.parse(alert.started_at) : durationMinutes(alert, now);
}

export function AlertsScreen({ client }: { client: SignageOneClient }): ReactNode {
  const alerts = useAsync(() => client.listAlerts(), [client]);
  usePolling(alerts.reload, 30_000);
  const action = useAction();
  const [lastCheck, setLastCheck] = useState<AlertCheckResult[] | null>(null);

  const items = alerts.data?.items ?? [];
  const firing = items.filter((item) => item.status === 'firing');
  /* 기록 표의 정렬·필터 — 필터는 주소에(`?state=·rule=`), 정렬은 브라우저에. 기본은 최근 시작 순. */
  const url = useUrlQuery();
  const stateChosen = url.list('state');
  const ruleChosen = url.list('rule');
  const [sort, toggleSort] = useTableSort<AlertSortKey>('alerts', ALERT_SORT_KEYS, { key: 'started', dir: 'desc' });
  const rows = sortRows(
    applyFilters(items, { state: stateChosen, rule: ruleChosen }, (alert, key) => (key === 'state' ? alert.status : alert.rule)),
    sort,
    (alert, key) => alertSortValue(alert, key),
  );
  const filtering = stateChosen.length + ruleChosen.length > 0;
  const sorting = (dirLabels: Record<SortDir, string>, firstDir: SortDir = 'asc') => ({
    sort,
    onSort: toggleSort,
    dirLabels,
    firstDir,
  });
  const monitor = alerts.data?.monitor;

  return (
    <div className="screen">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

      <Panel
        title={`발생 중 · ${firing.length}건`}
        subtitle={
          monitor
            ? monitor.interval_sec > 0
              ? thresholdsLine(monitor)
              : '주기 점검 꺼짐(MONITOR_INTERVAL_SEC=0) · [지금 점검]으로만 확인'
            : undefined
        }
        help="서버가 주기적으로 규칙을 점검해 장애를 기록합니다. 이 화면을 열어 두지 않아도 기록됩니다."
        actions={
          <button
            type="button"
            className="btn"
            disabled={action.pending}
            onClick={() =>
              void action.run(
                () => client.checkAlerts(),
                (result) => {
                  setLastCheck(result.results);
                  alerts.reload();
                },
              )
            }
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> 지금 점검
          </button>
        }
      >
        {alerts.loading && !alerts.data ? (
          <Loading />
        ) : firing.length === 0 ? (
          <p className="hint p-4">발생 중인 장애가 없습니다.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {firing.map((alert) => (
              <li key={alert.id} className="flex flex-wrap items-baseline gap-2 px-4 py-3">
                <Badge tone="danger">{alert.rule_label}</Badge>
                <span className="text-[13.5px] font-medium text-slate-800">{alert.summary}</span>
                {/* 꺼진 화면은 이름으로 바로 간다 — 상세에서 마지막 신호·정비 모드·오류를 본다. */}
                {offlineDisplays(alert).length > 0 ? (
                  <span className="flex basis-full flex-wrap gap-x-3 gap-y-1 text-[12.5px]">
                    {offlineDisplays(alert).map((display) => (
                      <Link key={display.id} className="link" to={`/displays/${display.id}`}>
                        {display.name || '(이름 없음)'}
                        {display.location ? <span className="cell-muted"> · {display.location}</span> : null}
                      </Link>
                    ))}
                  </span>
                ) : null}
                <span className="hint">
                  {when(alert.started_at)}부터 · {duration(alert)}째
                </span>
              </li>
            ))}
          </ul>
        )}
        {lastCheck ? (
          <div className="border-t border-slate-100 px-4 py-3">
            <p className="hint mb-1">점검 결과</p>
            <ul className="space-y-0.5 text-[12.5px]">
              {lastCheck.map((result) => (
                <li key={result.rule}>
                  <Badge tone={result.active ? (result.transition === 'pending' ? 'warn' : 'danger') : 'ok'}>
                    {TRANSITION_LABEL[result.transition]}
                  </Badge>{' '}
                  {result.summary}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Panel>

      <Panel
        title="장애 기록"
        className="screen-fill"
        subtitle="최근 100건"
        help="서버 로그에도 ‘장애 발생’·‘장애 해소’로 남습니다. 정비 모드 ▸ 플러그인에서 ‘장애 감시’를 끄면 새 기록이 쌓이지 않습니다."
      >
        {filtering && items.length > 0 ? (
          <FilterNote shown={rows.length} total={items.length} unit="건" onClear={() => url.set({ state: null, rule: null })} />
        ) : null}
        {items.length === 0 ? (
          <p className="hint p-4">기록된 장애가 없습니다.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <ColumnHead
                    label="상태"
                    column="state"
                    filter={
                      <FilterMenu
                        label="상태"
                        options={filterOptions(
                          items,
                          (alert) => alert.status,
                          (value) => ({ label: value === 'firing' ? '발생 중' : '해소' }),
                          stateChosen,
                          fixedOrder(['firing', 'resolved']),
                        )}
                        chosen={stateChosen}
                        onChange={(chosen) => url.set({ state: chosen })}
                      />
                    }
                  />
                  <ColumnHead
                    label="규칙"
                    column="rule"
                    filter={
                      <FilterMenu
                        label="규칙"
                        options={filterOptions(
                          items,
                          (alert) => alert.rule,
                          (value) => ({ label: items.find((alert) => alert.rule === value)?.rule_label ?? value }),
                          ruleChosen,
                        )}
                        chosen={ruleChosen}
                        onChange={(chosen) => url.set({ rule: chosen })}
                      />
                    }
                  />
                  <th>내용</th>
                  <ColumnHead label="시작" column="started" sorting={sorting({ asc: '오래된 순', desc: '최근 순' }, 'desc')} />
                  <ColumnHead label="지속" column="duration" sorting={sorting({ asc: '짧은 순', desc: '긴 순' }, 'desc')} />
                  <th>
                    알림
                    <HelpTip
                      text="메일·메신저·SMS 발송 결과입니다. 채널은 개발자가 정비 모드 ▸ 플러그인에서 설정하며, ‘장애 알림 발송’을 끄면 발송만 멈춥니다."
                      label="알림 도움말"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr className="row-empty">
                    <td colSpan={6}>필터 조건에 맞는 기록이 없습니다.</td>
                  </tr>
                ) : null}
                {rows.map((alert) => (
                  <tr key={alert.id} style={{ cursor: 'default' }}>
                    <td>
                      <Badge tone={alert.status === 'firing' ? 'danger' : 'ok'}>{alert.status === 'firing' ? '발생 중' : '해소'}</Badge>
                    </td>
                    <td className="cell-strong">{alert.rule_label}</td>
                    <td>{alert.summary}</td>
                    <td className="cell-muted">{when(alert.started_at)}</td>
                    <td className="cell-muted">{duration(alert)}</td>
                    <td className="cell-muted">
                      {alert.notifications.length === 0
                        ? '—'
                        : alert.notifications.map((item, index) => (
                            <span key={index} className="mr-1.5 inline-block" title={item.error || undefined}>
                              {CHANNEL_LABEL[item.channel]} {item.ok ? '✓' : '✗'}
                            </span>
                          ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
